/**
 * AdminCmsPageEdit — create + edit a CMS page with the TipTap editor.
 * Route: /admin/cms/pages/:id  (special-case id="new" = create mode)
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import RichTextEditor from "@/components/newspulse/RichTextEditor";
import { cmsApi } from "@/lib/cmsApi";
import { ArrowLeft, Loader2, Save, Trash2, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";

const EMPTY = { slug: "", title: "", portal_slug: "socialpulse", body_html: "", meta_description: "", status: "draft" };

const inputCls = "w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-blue-500 focus:outline-none";

export default function AdminCmsPageEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth() || {};
  const isNew = id === "new";
  const [page, setPage] = useState(isNew ? EMPTY : null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (isNew || !user) return;
    cmsApi.read(id).then(setPage).catch((e) => setErr(e.message));
  }, [id, isNew, user]);

  const canSee = user && (user.is_admin || user.platform_role === "editor" || user.platform_role === "super_admin");
  if (authLoading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!canSee)     return <div className="p-12 text-center text-slate-500">Editors + admins only.</div>;
  if (!page)       return <div className="p-12 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading page…</div>;

  const set = (k, v) => setPage((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setOk(null); setErr(null);
    try {
      const payload = {
        slug: page.slug.trim().toLowerCase(),
        title: page.title.trim(),
        portal_slug: page.portal_slug || "socialpulse",
        body_html: page.body_html || "",
        meta_description: page.meta_description || null,
        status: page.status || "draft",
      };
      const res = isNew ? await cmsApi.create(payload) : await cmsApi.update(id, payload);
      setOk(isNew ? "Page created." : "Saved.");
      if (isNew && res.page?.id) setTimeout(() => nav(`/admin/cms/pages/${res.page.id}`), 500);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this page? This can't be undone.")) return;
    try { await cmsApi.remove(id); nav("/admin/cms/pages"); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div className="max-w-[960px] mx-auto px-4 lg:px-6 py-8" data-testid="admin-cms-edit">
      <Link to="/admin/cms/pages" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> All pages
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
            style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
          {isNew ? "New page" : page.title || "Untitled"}
        </h1>
        <div className="flex gap-2">
          {!isNew && page.status === "published" && (
            <a href={`/p/${page.slug}`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors"
               data-testid="cms-view-public">
              View public <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {!isNew && (
            <button onClick={remove}
                    className="inline-flex items-center gap-1 border border-rose-200 hover:bg-rose-50 text-rose-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors"
                    data-testid="cms-delete-btn">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <button onClick={save} disabled={saving || !page.slug || !page.title}
                  className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors"
                  data-testid="cms-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : (isNew ? "Create" : "Save")}
          </button>
        </div>
      </div>

      {ok  && <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-900 flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5" /> {ok}</div>}
      {err && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 px-4 py-2 text-sm text-rose-900 flex gap-2"><AlertCircle className="w-4 h-4 mt-0.5" /> {err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <label className="block md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">Title</div>
          <input className={inputCls} value={page.title} onChange={(e) => set("title", e.target.value)}
                 placeholder="About Us" data-testid="cms-input-title" />
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">Status</div>
          <select className={inputCls} value={page.status} onChange={(e) => set("status", e.target.value)}
                  data-testid="cms-input-status">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <label className="block md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">Slug (URL path after /p/)</div>
          <input className={inputCls} value={page.slug} onChange={(e) => set("slug", e.target.value)}
                 placeholder="about" data-testid="cms-input-slug" />
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">Portal</div>
          <input className={inputCls} value={page.portal_slug} onChange={(e) => set("portal_slug", e.target.value)}
                 placeholder="socialpulse" data-testid="cms-input-portal" />
        </label>
        <label className="block md:col-span-3">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">Meta description (SEO)</div>
          <input className={inputCls} value={page.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)}
                 maxLength={320} placeholder="~160 chars — shows in Google search results"
                 data-testid="cms-input-meta" />
        </label>
      </div>

      <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">Body</div>
      <RichTextEditor
        value={page.body_html || ""}
        onChange={(html) => set("body_html", html)}
        placeholder="Write the page content…"
        testId="cms-body-editor"
      />
    </div>
  );
}
